import {
  type Component,
  For,
  Show,
  createEffect,
  createMemo,
  createResource,
  onCleanup,
} from "solid-js";

import { fetchAccess, fetchReady } from "@/api/client";
import type { ClusterStatus, PlatformSummary, VmRecord } from "@/api/generated";
import { fetchClusterStatus, fetchPlatformSummary } from "@/api/platform";
import { listVms } from "@/api/vms";
import type { AppRoute } from "@/appRoutes";
import { useAuth } from "@/auth/AuthProvider";
import { useActivity } from "@/dashboard/ActivityProvider";

interface Props {
  navigate: (route: AppRoute) => void;
}

// Minimal information dashboard rendered into the neuro shell.
// Severity is text + border style only — no accent colors. The
// service tiles condense to one row of stats each; the legacy
// "open service →" CTAs are gone since the wordmark menu owns
// navigation. Activity feed is a flat table; spend section is
// suppressed until billing lands real numbers.
const DashboardPage: Component<Props> = (props) => {
  const auth = useAuth();
  const [ready] = createResource(fetchReady);
  const [access] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => fetchAccess(),
  );
  const [vms] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => listVms(),
  );
  const [summary] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => fetchPlatformSummary(),
  );
  const [cluster, { refetch: refetchCluster }] = createResource(
    () => (auth.isAuthenticated() ? "fetch" : null),
    () => fetchClusterStatus(),
  );

  const { rows: activity, streamLive } = useActivity();

  const vmSummary = createMemo(() => summarizeVms(vms() ?? []));
  const apiOk = () => ready()?.status === "ok";

  createEffect(() => {
    if (!auth.isAuthenticated()) return;
    const id = setInterval(() => refetchCluster(), 10_000);
    onCleanup(() => clearInterval(id));
  });

  return (
    <div class="neuro-page">
      <div class="neuro-surface">
        <div class="neuro-route-body">
          <h2>Dashboard</h2>
          <p>{apiOk() ? "control plane online" : "control plane checking"}</p>

          <h3>Chat</h3>
          <div class="neuro-stat-grid">
            <Stat
              label="model"
              value={modelLabel(cluster())}
              meta={chatNote(cluster())}
            />
            <Stat label="threads" value={threadsLabel(summary())} />
            <Stat label="tok / s" value={tokSLabel(cluster())} />
          </div>

          <h3>Compute</h3>
          <div class="neuro-stat-grid">
            <Stat
              label="active"
              value={String(summary()?.vms.running ?? vmSummary().running)}
            />
            <Stat label="booting" value={String(summary()?.vms.booting ?? 0)} />
            <Stat
              label="quota"
              value={access() ? `${access()?.max_vms} vm` : "checking"}
            />
            <Show when={summary()?.vms.failed}>
              <Stat
                label="failed"
                value={String(summary()?.vms.failed ?? 0)}
                meta="vm in failed state"
              />
            </Show>
          </div>

          <h3>CLI</h3>
          <div class="neuro-stat-grid">
            <Stat label="auth" value="device-flow" />
            <Stat label="runs" value="—" meta="telemetry pending" />
          </div>

          <h3>Activity {streamLive() ? "· live" : "· idle"}</h3>
          <table class="neuro-table">
            <thead>
              <tr>
                <th>source</th>
                <th>when</th>
                <th>event</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={activity().length > 0}
                fallback={
                  <tr>
                    <td class="muted">api</td>
                    <td class="muted">{ready()?.status ?? "checking"}</td>
                    <td class="muted">
                      {apiOk()
                        ? "no activity yet — try launching a VM"
                        : "control plane readyz not ok"}
                    </td>
                  </tr>
                }
              >
                <For each={activity()}>
                  {(row) => (
                    <tr>
                      <td class="muted">{row.source}</td>
                      <td class="muted">{formatRelative(row.at)}</td>
                      <td>{row.message}</td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>

          <h3>Spend · today</h3>
          <p>billing API pending — figures unavailable.</p>
          <div class="neuro-stat-grid">
            <Stat label="chat · inference" value="pending" />
            <Stat label="compute · vm" value="pending" />
            <Stat label="cli · runs" value="pending" />
          </div>

          <div style="display: flex; gap: 12px; margin-top: 8px;">
            <button
              type="button"
              class="neuro-action"
              onClick={() => props.navigate("chat")}
            >
              open chat
            </button>
            <button
              type="button"
              class="neuro-action"
              onClick={() => props.navigate("compute")}
            >
              open compute
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat: Component<{ label: string; value: string; meta?: string }> = (
  props,
) => (
  <div class="neuro-stat">
    <span class="neuro-stat-label">{props.label}</span>
    <span class="neuro-stat-value">{props.value}</span>
    <Show when={props.meta}>
      <span class="neuro-stat-meta">{props.meta}</span>
    </Show>
  </div>
);

function summarizeVms(vms: VmRecord[]) {
  return {
    total: vms.length,
    running: vms.filter((vm) => vm.state === "running").length,
  };
}

function threadsLabel(summary: PlatformSummary | undefined): string {
  if (summary?.conversations === undefined) return "session-local";
  if (summary.conversations === null) return "unavailable";
  return `${summary.conversations} saved`;
}

function modelLabel(cluster: ClusterStatus | undefined): string {
  if (!cluster) return "checking…";
  return cluster.model ?? "unreachable";
}

function tokSLabel(cluster: ClusterStatus | undefined): string {
  if (!cluster || cluster.tokens_per_sec === null) return "—";
  return cluster.tokens_per_sec.toFixed(1);
}

function chatNote(cluster: ClusterStatus | undefined): string {
  if (!cluster) return "probing inference cluster…";
  if (!cluster.healthy) return "vLLM unreachable";
  if (cluster.tokens_per_sec === null) return "loaded · awaiting first gen";
  return `live · ${cluster.tokens_per_sec.toFixed(1)} tok/s`;
}

function formatRelative(rfc3339: string): string {
  const then = new Date(rfc3339).getTime();
  if (Number.isNaN(then)) return rfc3339;
  const delta = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (delta < 5) return "now";
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86_400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86_400)}d ago`;
}

export default DashboardPage;
